import React, { useState } from 'react'
import { Formik } from 'formik'
import { useQuery, useMutation } from  '../../Hooks/graphql_utils'
import { gql } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../Hooks/toast'
import { DailyBatchesSchema } from '@mmg/validation'
import { DailyBatchesForm } from './DailyBatchesForm.js'
import { ErrorMessage, Spinner } from '../../Elements'


const FRAGMENT = gql`
    fragment dailyBatchesFragment on Job {
        id
        company {
            company_name
        }
        name
        address_line_1
        address_line_2
        city
        sub_jobs {
            id
            mmg_pebble_perimeter
            mmg_ia
            depth_low
            depth_high
            depth_mid
            awaiting_builder_date
            job_types{
                name
            }
            job_category {
                id
                name
            }
            status {
                id
                name
            }
            pebble_details {
                id
                request_date
                app_date
                total_batches
                color_additions {
                    id
                    job_detail_id
                    material_type{
                        id
                        name
                    }
                    color {
                        id
                        name
                        material_type {
                            id
                            name
                        }
                    }
                    additions {
                        id
                        name
                    }
                    pool_section {
                        id
                        name
                    }
                    batches_used
                    notes
                }
                crew_information {
                    id
                    job_detail_id
                    crew {
                        id
                        name
                    }
                    foreman {
                        id
                        name
                        crew_types {
                            id
                        }
                    }
                    date
                    type
                    status {
                        id
                        name
                    }
                    phase_type {
                        id
                        name
                    }
                    job_order {
                        id
                        name
                    }

                    slurry_pickup {
                        id
                        name
                    }
                    notes
                }
            }
        }
    }
`

const QUERY = gql`
    ${FRAGMENT}
    query dailyBatchesQuery ($id: ID!) {
        job(id: $id) {
            ...dailyBatchesFragment
        }
        lookupJobStatus {
            id
            name
            show_pebble

            show_pebble_prep
            show_pebble_application
            show_pebble_detail
            show_pebble_slurry
        }
        lookupPebbleColor {
            id
            name
            material_type {
                id
                name
            }
        }
        lookupPebbleColorAdditive {
            id
            name
        }
        lookupPebbleColorMaterialType {
            id
            name
        }       
        lookupPoolSection {
            id
            name
        }
        lookupCrewTypes {
            id
            name
            lookup_company_division_id
            subdivision
            is_active
        }
        lookupEmployee {
            id
            name
            crew_types {
                id
            }
        }
        lookupPebblePhaseType {
            id
            name
        }
        lookupJobOrder {
            id
            name
        }
        lookupSlurryPickup {
            id
            name
            is_active
        }
    }
`

const MUTATION = gql`
    mutation UpdateDailyBatches($id: ID!, $dailyBatches: DailyBatchesInput!) {
        updateDailyBatches(id: $id, dailyBatches: $dailyBatches) {
            ...dailyBatchesFragment
        }
    }
    ${FRAGMENT}
`

export default () => {
    const { jobID } = useParams()
    const navigate = useNavigate()
    const { showSuccess, showError } = useToast()
    const [updateDailyBatches] = useMutation(MUTATION)
    const [trackedInitialValues, setTrackedInitialValues] = useState(null)

    const { loading, error, data } = useQuery(QUERY, {
        variables: {
            id: jobID
        },
        onCompleted: (data) => setTrackedInitialValues(data.job)
    })

    if (loading || !trackedInitialValues) {
        return (
            <Spinner />
        )
    }

    if (error) {
        return (
            <ErrorMessage />
        )
    }
    const onSubmit = async (values, { setValues }) => {
        values.sub_jobs = values.sub_jobs.filter(sj => sj.job_category && sj.job_category.id && sj.job_category.id === 2)
        const model = DailyBatchesSchema.cast(values, { stripUnknown: true })

        await updateDailyBatches({
            variables: {
                id: jobID,
                dailyBatches: model
            }
        })
            .then(({ data }) => {
                setTrackedInitialValues(data.updateDailyBatches)
                setValues(data.updateDailyBatches)
                showSuccess('Success!')
            })
            .catch(() => showError('Error, please try again.'))
    }

    const onReset = () => navigate('/job')

    return (
        <Formik
            initialValues={trackedInitialValues}
            onSubmit={onSubmit}
            enableReinitialize
            render={props => <DailyBatchesForm
                {...props}
                employees={data.lookupEmployee}
                crewTypes={data.lookupCrewTypes}
                jobStatuses={data.lookupJobStatus}
                pebbleColors={data.lookupPebbleColor}
                poolSections={data.lookupPoolSection}
                pebblePhases={data.lookupPebblePhaseType}
                pebbleColorAdditives={data.lookupPebbleColorAdditive}
                pebbleColorMaterialTypes={data.lookupPebbleColorMaterialType}
                jobOrders={data.lookupJobOrder}
                slurryPickupOptions={data.lookupSlurryPickup.filter(s => s.is_active)}
                onCancel={onReset}
            />
            }
        />
    );
}